/**
 * Staff Dashboard widget: PII Encryption Status.
 *
 * Renders only for super_admin (the only role that can call
 * `/api/admin/pii-encryption-status`). Silent on 403/error so lower
 * roles' Dashboards stay clean.
 */
import { useEffect, useState } from 'react';
import { Shield, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtNumber } from '../lib/localeUtils';

export default function PIIEncryptionStatusWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const canView = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    api.get('/admin/pii-encryption-status')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canView]);

  if (!canView || !data) return null;

  const pct = data.totals?.coverage_pct ?? 0;
  const encrypted = data.totals?.encrypted_rows ?? 0;
  const plaintext = data.totals?.plaintext_rows ?? 0;
  const compliant = plaintext === 0;

  return (
    <Card
      data-testid="pii-encryption-widget"
      className={`transition-all ${compliant ? 'border-emerald-200' : 'border-amber-300 shadow-[0_4px_24px_-8px_rgba(217,119,6,0.25)]'}`}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-xl grid place-items-center ${compliant ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
              {compliant ? <Shield className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
            </div>
            <div>
              <div className="font-semibold text-slate-900">PII Encryption Status</div>
              <div className="text-xs text-slate-500">
                AES-256-GCM · {fmtNumber(encrypted)} encrypted · {fmtNumber(plaintext)} plaintext
              </div>
            </div>
          </div>
          <Badge
            className={compliant ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}
            data-testid="pii-coverage-pct"
          >
            {pct}%
          </Badge>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mb-4" aria-hidden>
          <div
            className={`h-full rounded-full transition-all ${compliant ? 'bg-emerald-500' : 'bg-amber-500'}`}
            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
          />
        </div>

        {/* Per-collection breakdown */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {(data.collections || []).map((c) => {
            const ok = c.plaintext === 0;
            return (
              <div
                key={c.collection}
                data-testid={`pii-row-${c.collection}`}
                className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                  ok ? 'border-slate-100 hover:bg-slate-50' : 'border-amber-200 bg-amber-50/40'
                }`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-slate-800 truncate">{c.label}</div>
                  <div className="text-[11px] text-slate-500">
                    {fmtNumber(c.encrypted)} enc · {fmtNumber(c.plaintext)} plain · {fmtNumber(c.empty)} n/a
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <ShieldAlert className="w-4 h-4 text-amber-500" />}
                  <span className={`text-sm font-semibold tabular-nums ${ok ? 'text-emerald-700' : 'text-amber-700'}`}>{c.coverage_pct}%</span>
                </div>
              </div>
            );
          })}
        </div>

        {!compliant && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Some rows still hold plaintext PII. Run
            <code className="font-mono text-[11px] px-1 mx-1 bg-white/60 rounded">python scripts/migrate_portal_pii.py</code>
            to upgrade.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
